import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  getUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  setPage,
  setPageSize,
  setUnitSearch,
} from "./property-units.slice";
import { useCallback, useEffect, useState } from "react";
import { PropertyUnit } from "@/models/properties.model";
import { uuid } from "@/interfaces";
import type { UnitForm } from "@/repositories/unit-records";

export function usePropertyUnits(propertyId: string) {
  const dispatch = useAppDispatch();

  const {
    units,
    total,
    filters: { page, pageSize, unitNumber },
    loading,
    saving,
    deleting,
  } = useAppSelector((state) => state.propertyUnits);

  const [editing, setEditing] = useState<PropertyUnit | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<uuid | null>(null);

  // Load units when page, size, search, or propertyId changes
  useEffect(() => {
    dispatch(getUnits(propertyId));
  }, [propertyId, page, pageSize, unitNumber, dispatch]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await dispatch(deleteUnit({ propertyId, id })).unwrap();
        // TODO: Toast/sonner SYN-64
        dispatch(getUnits(propertyId));
      } catch (e) {
        // TODO: Toast/sonner SYN-64
        console.error(e);
      }
    },
    [dispatch, propertyId]
  );

  const handleSave = async (data: UnitForm & { property: uuid }) => {
    try {
      await dispatch(createUnit(data)).unwrap();
      // TODO: Toast/sonner SYN-64
      setCreating(false);
      dispatch(getUnits(propertyId));
    } catch {
      // TODO: Toast/sonner SYN-64
    }
  };

  const handleUpdate = async (id: string, updates: UnitForm) => {
    try {
      await dispatch(updateUnit({ propertyId, id, updates })).unwrap();
      // TODO: Toast/sonner SYN-64
      setEditing(null);
      dispatch(getUnits(propertyId));
    } catch {
      // TODO: Toast/sonner SYN-64
    }
  };

  return {
    units,
    total,
    page,
    pageSize,
    unitNumber,
    loading,
    saving,
    deleting,
    setPage: (page: number) => dispatch(setPage(page)),
    setPageSize: (size: number) => dispatch(setPageSize(size)),
    setSearch: (value: string) => dispatch(setUnitSearch(value)),
    handleDelete,
    handleSave,
    handleUpdate,
    creating,
    editing,
    openCreateModal: () => setCreating(true),
    closeCreateModal: () => setCreating(false),
    openEditModal: (unit: PropertyUnit) => setEditing(unit),
    closeEditModal: () => setEditing(null),
    showDeleteConfirm,
    setShowDeleteConfirm,
  };
}
